// PDM-2749: the "Other" tab of the hospitalization list - structured ICD diagnoses (OR within the list), the
// patient's age, weight and BMI as inclusive ranges, and the death options (OR). All of them are independent
// conditions ANDed by the backend.
//
// Own module like the biochemistry helpers: the list layout and the xlsx export hook both build the request from
// these and importing one from the other would close an import cycle.

/** The slice of the hospitalization list filters these helpers read. Structural, so both callers just pass theirs. */
export type OtherFilterValues = {
  icdCodes?: string[];
  ageFrom?: number;
  ageTo?: number;
  weightFrom?: number;
  weightTo?: number;
  bmiFrom?: number;
  bmiTo?: number;
  deathDuringHospitalizations?: string[];
};

/**
 * Fixed slider bounds of the patient attributes (PDM-2874), no longer the maxima from the data - the range component
 * clamps typed values to them too. Age 120: the oldest verified human lived to 122. Weight 300 kg: hospitalized
 * patients over 250 kg are extremely rare. BMI 120: a 300 kg patient 1.60 m tall has a BMI of about 117.
 */
export const AGE_RANGE = {min: 0, max: 120};
export const WEIGHT_RANGE = {min: 0, max: 300};
export const BMI_RANGE = {min: 0, max: 120};

function joined(values: string[] | undefined): string | undefined {
  return values !== undefined && values.length > 0 ? values.join(',') : undefined;
}

/**
 * Request parameters of the tab, lists as comma separated strings, unset bounds left out. The list query and the
 * xlsx export both go through here so the two can never disagree about what is being filtered.
 */
export function otherParams(filters: OtherFilterValues): Record<string, string | number | undefined> {
  return {
    icdCodes: joined(filters.icdCodes),
    ageFrom: filters.ageFrom,
    ageTo: filters.ageTo,
    weightFrom: filters.weightFrom,
    weightTo: filters.weightTo,
    bmiFrom: filters.bmiFrom,
    bmiTo: filters.bmiTo,
    deathDuringHospitalizations: joined(filters.deathDuringHospitalizations),
  };
}

/**
 * How an ICD code reads in the dropdown and on its chip: the code first (that is what users search by), the
 * hospital's description after it when the hospital sent one - MERIE's icd-codes endpoint has no catalogue behind it.
 */
export function formatIcdOptionLabel(code: string, label: string | null | undefined): string {
  return label ? `${code} – ${label}` : code;
}
